import { createServerFn } from "@tanstack/react-start";

import { sessionRequestMiddleware } from "@/api/middleware/auth";
import type { ResolvedSession } from "@/server/use-cases/auth/resolve-session";
import type { Audience, ProviderId } from "@/lib/identity";

/**
 * The one identity shape the browser ever sees.
 *
 * The nested split is the domain model, not tidying: `user` is who you are —
 * stable across sessions — while `session` is what *this* session grants.
 * `groups`/`roles` are session-minted facts, stamped from the provider's claims
 * at login, so they sit under `session` rather than on the user.
 *
 * `session.id` is the `sessions` row id, not the session cookie's token (the
 * cookie's value is only ever stored hashed). Exposing the row id does not let
 * the browser authenticate as the session: it travels to Python as the
 * `X-Atlas-Bff-Session-Id` auth carrier (system.md §3.1) only alongside an
 * HMAC signature the browser cannot produce. It is not a tracing key either —
 * that is `X-Atlas-Request-Id` (system.md §3.3), which is what the logs carry.
 * It is here because a session's grants are scoped to it, so the UI can key
 * per-session state on the session rather than on the user.
 *
 * The access token and its expiry stay server-side entirely — the browser has
 * no consumer for that token metadata, and shipping it is disclosure for
 * nothing.
 */
export type CurrentUser = {
  user: {
    id: string;
    email: string;
    name: string | null;
    provider: ProviderId;
    providerUserId: string;
    providerTenantId: string | null;
    audience: Audience;
  };
  session: {
    id: string;
    groups: string[];
    roles: string[];
  };
};

/**
 * The projection, as a plain function so it is unit-testable without a request:
 * one place decides what crosses the browser↔BFF seam, and nothing has to reach
 * through a server function to check it.
 */
export function toCurrentUser(session: ResolvedSession | null): CurrentUser | null {
  if (!session) return null;
  return {
    user: {
      id: session.user.id,
      email: session.user.email,
      name: session.user.name,
      provider: session.user.provider,
      providerUserId: session.user.providerUserId,
      providerTenantId: session.user.providerTenantId,
      audience: session.user.audience,
    },
    session: {
      id: session.sessionId,
      groups: session.groups,
      roles: session.roles,
    },
  };
}

// Error scrubbing is global function middleware (start.ts); no per-function
// wrapper needed. sessionRequestMiddleware is deduped against the global
// request-layer run, so this types the session context without re-resolving.
//
// sessionRequestMiddleware, not requireAuth: the _authed gate needs `null` back
// so it can redirect to /login. A thrown 401 would surface as a query error
// instead of a redirect.
export const getCurrentUser = createServerFn({ method: "GET" })
  .middleware([sessionRequestMiddleware])
  .handler(({ context }: { context: { session: ResolvedSession | null } }) =>
    toCurrentUser(context.session),
  );

// Sign-out is a server function, not a route, because it is never a navigation —
// only ever a fetch from `useSignOut`. Being a server function puts it inside
// both global middlewares structurally: the CSRF check (a cross-site POST can no
// longer log a user out) and the error scrub.
//
// No `requireAuth`: an expired session must still be able to sign out cleanly.
// A 401 here would leave the browser holding a dead cookie and the UI stuck on
// an authed page. `logout` is idempotent — with no cookie it just clears.
export const signOut = createServerFn({ method: "POST" }).handler(async () => {
  const { logout } = await import("@/server/use-cases/auth/logout");
  await logout();
});
